import { useRef } from "react";

/**
 * Compact in-card toggle (radiogroup). Arrow keys/Home/End move and select.
 *
 * @param {object} props
 * @param {{value: string, label: React.ReactNode}[]} props.options
 * @param {string} props.value Selected option value.
 * @param {(value: string) => void} props.onChange
 * @param {string} [props["aria-label"]] Accessible name of the group (recommended).
 * @param {boolean} [props.fullWidth=false] Stretches the group to its container; options share the width equally.
 * @param {boolean} [props.disabled=false] Dims the group and ignores clicks and keys. Uses aria-disabled
 *   (not native disabled) so focus stays put while, e.g., a save triggered by the control is in flight.
 * @param {string} [props.className]
 */
const SegmentedControl = ({
  options,
  value,
  onChange,
  "aria-label": ariaLabel,
  fullWidth = false,
  disabled = false,
  className = "",
}) => {
  const optionRefs = useRef([]);
  const selectedIndex = options.findIndex((o) => o.value === value);
  const focusIndex = selectedIndex === -1 ? 0 : selectedIndex;

  const handleKeyDown = (event, index) => {
    if (disabled) return;
    const last = options.length - 1;
    const targets = {
      ArrowRight: index === last ? 0 : index + 1,
      ArrowDown: index === last ? 0 : index + 1,
      ArrowLeft: index === 0 ? last : index - 1,
      ArrowUp: index === 0 ? last : index - 1,
      Home: 0,
      End: last,
    };
    const nextIndex = targets[event.key];
    if (nextIndex === undefined) return;
    event.preventDefault();
    optionRefs.current[nextIndex]?.focus();
    if (options[nextIndex].value !== value) onChange(options[nextIndex].value);
  };

  return (
    <div
      role="radiogroup"
      aria-label={ariaLabel}
      aria-disabled={disabled || undefined}
      className={[
        fullWidth ? "flex w-full" : "inline-flex",
        "rounded-control bg-surface-subtle p-1",
        disabled && "opacity-60",
        className,
      ]
        .filter(Boolean)
        .join(" ")}
    >
      {options.map((option, index) => {
        const selected = option.value === value;
        return (
          <button
            key={option.value}
            ref={(node) => {
              optionRefs.current[index] = node;
            }}
            type="button"
            role="radio"
            aria-checked={selected}
            tabIndex={index === focusIndex ? 0 : -1}
            aria-disabled={disabled || undefined}
            onClick={() => !disabled && !selected && onChange(option.value)}
            onKeyDown={(event) => handleKeyDown(event, index)}
            className={[
              "focus-ring whitespace-nowrap rounded-control px-3 py-1.5 text-sm font-medium transition-colors",
              fullWidth && "flex-1",
              disabled && "cursor-not-allowed",
              selected ? "bg-surface text-fg shadow-sm" : "text-fg-muted",
              !selected && !disabled && "hover:text-fg",
            ]
              .filter(Boolean)
              .join(" ")}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
};

export default SegmentedControl;
